import { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeInvalidation } from '../hooks/useRealtimeInvalidation';
import {
  fetchUnreadCriticalNotifications,
  markAsRead,
  formatTimeAgo,
  type Notification,
} from '../lib/notificationService';
import ModalPortal from './ModalPortal';

/**
 * Blokkerende pop-up voor kritieke notificaties (priority = 'critical'), zoals
 * IBAN-wijzigingen en betalingen zonder bon/factuur. Ontvangers zijn de rollen
 * Admin en Administratie (beslissing B13 uit het financieel masterplan). De
 * modal blijft staan tot elke melding expliciet met "Gezien" is bevestigd.
 */
export default function CriticalAlertsGate() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Notification[]>([]);
  const [marking, setMarking] = useState<string | null>(null);

  const isRecipientRole = profile?.role === 'Admin' || profile?.role === 'Administratie';

  const loadAlerts = useCallback(async () => {
    if (!user || !isRecipientRole) return;
    try {
      const data = await fetchUnreadCriticalNotifications();
      setAlerts(data);
    } catch {
      // Stil falen (bv. zolang de priority-kolom nog niet is gemigreerd):
      // de gewone notificatiebel blijft als vangnet werken.
    }
  }, [user, isRecipientRole]);

  useRealtimeInvalidation('notifications', loadAlerts);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleSeen = async (alert: Notification) => {
    setMarking(alert.id);
    try {
      await markAsRead(alert.id);
      setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
    } catch (err) {
      console.error('Kritieke melding markeren als gelezen mislukt:', err);
    } finally {
      setMarking(null);
    }
  };

  const handleOpenLink = (alert: Notification) => {
    if (alert.link) navigate(alert.link);
  };

  if (!isRecipientRole || alerts.length === 0) return null;

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[110] flex items-center justify-center p-4"
        role="alertdialog"
        aria-modal="true"
        aria-label="Kritieke meldingen"
      >
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative bg-surface rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 bg-error/10 border-b border-error/30">
            <div className="w-10 h-10 rounded-full bg-error/15 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-5 h-5 text-error" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">
                {alerts.length === 1 ? 'Kritieke melding' : `${alerts.length} kritieke meldingen`}
              </h2>
              <p className="text-xs text-foreground-secondary">
                Deze meldingen vereisen directe aandacht van administratie/admin.
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-theme-border">
            {alerts.map((alert) => (
              <div key={alert.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{alert.title}</p>
                    <p className="text-sm text-foreground-secondary mt-1">{alert.body}</p>
                    <span className="text-[11px] text-foreground-tertiary mt-1 inline-block">
                      {formatTimeAgo(alert.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => handleSeen(alert)}
                    disabled={marking === alert.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-error text-white text-xs font-medium rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Gezien
                  </button>
                  {alert.link && (
                    <button
                      onClick={() => handleOpenLink(alert)}
                      className="px-3 py-1.5 text-xs font-medium text-foreground-secondary bg-surface-tertiary border border-theme-border rounded-lg hover:bg-surface-secondary transition-colors"
                    >
                      Bekijken
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
